import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../layout/Layout';
import Pagination from '../../common/Pagination';
import DateRangePicker from '../../common/DateRangePicker';
import EditInvoiceForm from '../EditInvoiceForm';
import InvoiceView from '../InvoiceView';
import invoicesCreateIcon from '../../../assets/invoicesCreateIcon.png';
import invoicesEditIcon from '../../../assets/invoicesEditIcon.png';
import invoicesPrintIcon from '../../../assets/invoicesPrintIcon.png';
import invoicesNotesIcon from '../../../assets/invoicesNotesIcon.png';
import invoicesMailIcon from '../../../assets/invoicesMailIcon.png';
import searchIcon from '../../../assets/navSearchIcon.png';
import backButtonIcon from '../../../assets/backButton.png';
import customerAddIcon from '../../../assets/customerAddIcon.png';
import { format, parse, parseISO } from 'date-fns';
import {
  getInvoices,
  getInvoice,
  updateInvoice,
  createInvoice,
  generateInvoicePdf,
  emailInvoice,
  deleteInvoice,
  InvoiceResponse,
  InvoicePagination,
  InvoiceOut,
  InvoiceCreate,
  InvoiceUpdate,
  InvoiceFilters,
  SaleTypeEnum,
  PaymentModeEnum
} from '../../../services/invoiceService';
import quoteService from '../../../services/quoteService';
import apiClient, { clearApiCache } from '../../../services/apiClient';
import { AUTH_TOKEN_KEY, API_URL, ENDPOINTS } from '../../../config';
import { CustomBackButton } from '../../ui/DesignSystem';
import PageLoadingSpinner from '../../ui/PageLoadingSpinner';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../common/Toast';
import { useStoreFilter } from '../../../hooks/useStoreFilter';
import { useAuth } from '../../../context/AuthContext';
import LoadingSpinner from '../../ui/LoadingSpinner';
import _ from 'lodash';
import ExitConfirmationDialog from '../../common/ExitConfirmationDialog';
import DeleteConfirmationDialog from '../../common/DeleteConfirmationDialog';

// Add CSS for spinner animation
const spinnerStyle = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject the styles into the document head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = spinnerStyle;
  if (!document.head.querySelector('style[data-spinner]')) {
    styleElement.setAttribute('data-spinner', 'true');
    document.head.appendChild(styleElement);
  }
}

const PageContainer = styled.div`
  padding: 0 32px 24px 32px;
`;

const PageTitle = styled.h1`
  color: #042B41;
  font-size: 32px;
  font-weight: 800;
  margin: 0;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 1rem;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 24rem;
  height: 36px;
  background: rgba(255, 255, 255, 1);
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 100%;
  padding: 10px 15px 10px 40px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  font-size: 14px;
  background: rgba(255, 255, 255, 1);

  &::placeholder {
    color: #9CA3AF;
    opacity: 0.6;
  }
`;

const DatePickerWrapper = styled.div`
  width: 245px;
  height: 36px;
  background: rgba(255, 255, 255, 1);

  /* Override DateRangePicker styles for this page */
  & > div {
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 1);
  }

  .calendar-icon {
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }

  span {
    opacity: 0.6;
  }
`;

// Add Store Type Dropdown styling
const StoreTypeDropdown = styled.select`
  width: 140px;
  height: 36px;
  padding: 8px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  background: white;
  color: #374151;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #042B41;
  }
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #042B41;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-left: auto;
  height: 36px;

  &:hover {
    background-color: #0A3D5A;
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  border: 1px solid #e0e0e0;
`;

const TableHeader = styled.thead`
  background-color: #042B41;

  th {
    padding: 10px 12px;
    text-align: center;
    font-weight: 500;
    height: 48px;
    vertical-align: middle;
    border: 1px solid #0A3D5A;
    color: white !important;
    font-size: 14px !important;
    font-weight: bold !important;
    
    &:nth-child(5) {
      text-align: right; /* Right-align the Grand Total header */
    }
  }
`;

const TableBody = styled.tbody`
  tr {
    background-color: white;
    border-bottom: 1px solid #e0e0e0;
    height: 48px;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: #f9fafb;
    }
  }

  td {
    padding: 10px 12px;
    color: #333;
    vertical-align: middle;
    border: 1px solid #e0e0e0;
    font-size: 14px;
    text-align: center;
    
    &:nth-child(5) {
      text-align: right; /* Right-align the Grand Total values */
    }
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 3px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
    filter: grayscale(100%);
  }
`;

const NotesIcon = styled.span<{ hasNotes?: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
    filter: ${props => props.hasNotes ? 'invert(15%) sepia(95%) saturate(6932%) hue-rotate(358deg) brightness(95%) contrast(112%)' : 'none'};
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

// Notes Modal Components
const NotesModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const NotesModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 20px;
`;

const NotesModalTitle = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin: 0 0 20px 0;
  text-align: center;
`;

const NotesTextarea = styled.textarea`
  width: 100%;
  height: 150px;
  padding: 12px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 20px;
  resize: none;

  &:focus {
    outline: none;
    border-color: #042B41;
  }
`;

const NotesButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
`;

const NotesButton = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  min-width: 120px;
`;

const SaveNotesButton = styled(NotesButton)`
  background-color: #042B41;
  color: white;
  border: none;

  &:hover {
    background-color: #031f30;
  }
`;

const CloseNotesButton = styled(NotesButton)`
  background-color: white;
  color: #333;
  border: 1px solid #e0e0e0;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  min-width: 120px;
`;

// Restore the original back button styling to match other pages
const StyledBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #042B41;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(4, 43, 65, 0.05);
  }
  
  svg {
    stroke-width: 2px;
  }
`;

// Add a styled component for the breadcrumb
const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 24px;
  font-weight: 600;
`;

const BreadcrumbItem = styled.span`
  font-weight: 600;
  color: #042B41;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:last-child {
    color: #6B7280;
    font-weight: 500;
    text-decoration: none;
    cursor: default;

    &:hover {
      text-decoration: none;
    }
  }
`;

const BreadcrumbSeparator = styled.span`
  color: #6B7280;
`;

// Interface definitions
interface Invoice {
  id: string;
  invoiceNo: string;
  customerName: string;
  storeType: string;
  date: string;
  total: string;
  notes?: string;
  status?: string;
  data?: InvoiceData;
  hasNotes?: boolean;
  deliverToAddress?: string; // Add missing property
  poNumber?: string; // Add missing property
}

interface InvoiceData {
  id?: string; // Add optional id field
  storeType: string;
  saleType: string;
  modeOfPayment: string;
  poNumber: string;
  invoiceTo: string;
  deliverToAddress: string;
  invoiceNo: string;
  notes: string;
  linkedQuote: string;
  date: string;
  dontSendPo: boolean;
  status?: string;
  items: InvoiceItem[];
  totals: {
    orderTotal: number;
    surcharge: number;
    gst: number;
    discount: number;
    shipping: number;
    grandTotal: number;
  };
}

interface InvoiceItem {
  id: string;
  sku: string;
  description: string;
  quantity: {
    units: number | null;
    boxes: number | null;
    pieces: number | null;
    m2: number | null;
    blm: number | null;
    total: number;
  };
  unitPrice: number;
  totalPrice: number;
  deductFromStock: boolean;
}

// Define Quote interface for the quotes state
interface Quote {
  id: string;
  quote_no: string;
  items?: any[];
  quote_items?: any[];
}

enum PageView {
  LIST,
  EDIT,
  CREATE
}

// Add the missing styled components
const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 6px;
  justify-content: center;
`;

const PaginationContainer = styled.div`
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
`;

// Notes column styling
interface NotesColumnProps {
  hasNotes?: boolean;
}

const NotesColumn = styled.td<NotesColumnProps>`
  cursor: pointer;
  padding: 10px 12px;
  color: #333;
  vertical-align: middle;
  border: 1px solid #e0e0e0;
  font-size: 14px;
  text-align: center;
  
  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
    filter: ${props => props.hasNotes ? 'invert(15%) sepia(95%) saturate(6932%) hue-rotate(358deg) brightness(95%) contrast(112%)' : 'none'};
  }
  
  &:hover img {
    opacity: 0.7;
  }
`;

const LoadingRow = styled.tr`
  td {
    text-align: center !important;
    padding: 40px 20px !important;
    border: none !important;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6B7280;
  font-weight: 500;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  border-radius: 4px;
  border: 1px solid;
  
  ${props => props.status === 'draft' || props.status === 'DRAFTED' ? `
    background-color: #fef2f2;
    color: #dc2626;
    border-color: #dc2626;
  ` : `
    background-color: #f0fdf4;
    color: #16a34a;
    border-color: #16a34a;
  `}
`;

const InvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const shouldCreateInvoice = searchParams.get('create') === 'true';
  const toast = useToast();
  const storeFilterHook = useStoreFilter();
  const { user } = useAuth();

  // Memoize storeFilter to prevent infinite re-renders
  const storeFilter = useMemo(() => ({
    shouldFilterByStore: storeFilterHook.shouldFilterByStore,
    userStoreType: storeFilterHook.userStoreType,
    userStoreTypeId: storeFilterHook.userStoreTypeId,
    isStoreRestricted: storeFilterHook.isStoreRestricted
  }), [
    storeFilterHook.shouldFilterByStore,
    storeFilterHook.userStoreType,
    storeFilterHook.userStoreTypeId,
    storeFilterHook.isStoreRestricted
  ]);

  // State management
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedDateRange, setSelectedDateRange] = useState<string>('');
  const [filters, setFilters] = useState<InvoiceFilters>({});
  const [error, setError] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<PageView>(PageView.LIST);
  const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null);
  const [currentInvoice, setCurrentInvoice] = useState<InvoiceOut | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);
  const [notesValue, setNotesValue] = useState<string>('');
  const [notesInvoiceId, setNotesInvoiceId] = useState<string | null>(null);
  const [isSavingNotes, setIsSavingNotes] = useState<boolean>(false);
  const [isViewInvoiceOpen, setIsViewInvoiceOpen] = useState<boolean>(false);
  const [viewInvoiceId, setViewInvoiceId] = useState<string | null>(null);
  const [emailingInvoiceId, setEmailingInvoiceId] = useState<string | null>(null);

  // Add store type filter state
  const [storeTypeFilter, setStoreTypeFilter] = useState<string>('All');

  // Track if form has unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  
  // Delete confirmation states
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<{ id: string, invoiceNo: string } | null>(null);

  // Track data fetching status without aggressive caching

  // Track if this is the first load or navigation to the page
  const isInitialLoadRef = useRef<boolean>(true);
  const lastPathnameRef = useRef<string>(location.pathname);

  // Quotes state
  const [quotes, setQuotes] = useState<Quote[]>([]);

  // Improved debounced search using useRef to avoid memory leaks
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Optimized debounced search handler
  const debouncedSearch = useCallback((searchValue: string) => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout with shorter delay for better UX
    searchTimeoutRef.current = setTimeout(() => {
      // Update filters
      setFilters(prevFilters => ({
        ...prevFilters,
        search: searchValue || undefined
      }));

      // Reset to first page
      setCurrentPage(1);
    }, 200); // Reduced from 300ms to 200ms for faster response
  }, []);

  // Helper function to show toast
  const showToast = (message: string, type: 'error' | 'success' | 'info' = 'error') => {
    toast.showToast(message, { type });
  };

  // Add a utility function to handle different quote ID formats
  const getQuoteIdForApi = (quoteId: string): string | undefined => {
    if (!quoteId || quoteId.trim() === '') {
      return undefined;
    }

    // Check if it's a valid UUID already
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(quoteId)) {
      return quoteId;
    }

    // Try to find the full UUID from the quotes array based on numeric ID or quote_no
    const foundQuote = quotes.find(q => q.id === quoteId || q.quote_no === quoteId);
    if (foundQuote) {
      return foundQuote.id;
    }

    // If we can't match it, return undefined to skip this field
    return undefined;
  };

  // Function to fetch quotes
  const fetchQuotes = async () => {
    setIsLoading(true);
    try {
      // Implement quote service call to fetch quotes
      const response = await quoteService.getCurrentQuotes({ limit: 100 });

      if (response && response.items) {
        setQuotes(response.items.map((quote: any) => ({
          id: quote.id,
          quote_no: quote.quote_no,
          items: quote.quote_items || []
        })));
      } else {
        setQuotes([]);
      }
    } catch (error) {
      // Keep quotes empty on error
      setQuotes([]);
    } finally {
      setIsLoading(false);
    }
  };

  // UseEffect to fetch quotes on component mount
  useEffect(() => {
    fetchQuotes();
  }, []);

  // Initialize date range on component mount
  useEffect(() => {
    // If navigated with a search param (e.g., from Global Search), skip applying default date filter
    const urlParams = new URLSearchParams(location.search);
    const initialSearch = urlParams.get('search');
    if (initialSearch && initialSearch.trim().length > 0) {
      // Prefill search box and filters, and clear any date limits so the invoice always shows
      setSearchQuery(initialSearch);
      setSelectedDateRange('');
      setFilters(prev => {
        const { start_date, end_date, ...rest } = prev;
        return { ...rest, search: initialSearch } as InvoiceFilters;
      });
      setCurrentPage(1);
      return; // Do not set default 30-day window
    }

    // Set default date range: 30 days in the past to today (inclusive)
    const today = new Date();
    const pastDate = new Date(today);
    pastDate.setDate(today.getDate() - 30);

    // Format dates in DD-MM-YYYY format for display
    const formattedStart = formatDate(pastDate);
    const formattedEnd = formatDate(today);

    // Set initial date range
    const initialDateRange = `${formattedStart} - ${formattedEnd}`;
    setSelectedDateRange(initialDateRange);

    // Also update filters with these dates
    setFilters({
      ...filters,
      start_date: formatDateForBackend(pastDate),
      end_date: formatDateForBackend(today)
    });
  }, []);

  // Respond to changes in ?search= while already on the page (e.g., from Global Search)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const incomingSearch = urlParams.get('search');
    if (incomingSearch && incomingSearch.trim().length > 0) {
      if (incomingSearch !== searchQuery) {
        setSearchQuery(incomingSearch);
      }
      // Clear date filters and set search filter
      setSelectedDateRange('');
      setFilters(prev => {
        const { start_date, end_date, ...rest } = prev;
        return { ...rest, search: incomingSearch } as InvoiceFilters;
      });
      setCurrentPage(1);
    }
  }, [location.search]);

  // Helper function to format date as DD/MM/YYYY
  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Format date as YYYY-MM-DD for backend
  const formatDateForBackend = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Updated memoizedFilters with stringified value instead of object references
  const memoizedFilters = useMemo(() => {
    const baseFilters = { ...filters };
    
    // Add store type filter if not 'All'
    if (storeTypeFilter !== 'All') {
      baseFilters.store_type = storeTypeFilter;
    }
    
    return baseFilters;
  }, [JSON.stringify(filters), storeTypeFilter]);

  // Simplified fetch function for smooth operation
  const fetchInvoices = useCallback(async (page: number, perPage: number, queryFilters: InvoiceFilters, bypassCache: boolean = false) => {
    // Check if there's a search query active
    const hasSearchQuery = queryFilters.search && queryFilters.search.length > 0;

    // Only show loading for non-search requests or first load to avoid flickering
    if (!hasSearchQuery || invoices.length === 0) {
      setIsLoading(true);
    }

    setError(null); // Clear any previous errors

    try {
      // Use the service function to get invoices with store filtering
      // Pass bypassCache to force fresh API call when page loads
      const data = await getInvoices(page, perPage, queryFilters, storeFilter, bypassCache);

      if (!data || !data.items) {
        setError("Failed to load invoices: invalid data format");
        setInvoices([]);
        setTotalItems(0);
        return;
      }

      // Clear error state explicitly on successful data fetch
      setError(null);

      // Check if we have invoice items in the response
      if (data.items.length === 0) {
              setInvoices([]);
      setTotalItems(data.total || 0);
      return;
      }

      // Map API response to UI model
      const mappedInvoices = data.items.map((invoice: InvoiceResponse) => {
        // Handle grand_total correctly whether it's a string or number
        const total = typeof invoice.grand_total === 'number'
          ? invoice.grand_total.toFixed(2)
          : (parseFloat(invoice.grand_total || '0') || 0).toFixed(2);

        // Get invoice number from nested object or use invoice_no as fallback
        const invoiceNo = invoice.invoice_number?.formatted_number ||
          (typeof invoice.invoice_number === 'object' && 'id' in invoice.invoice_number
            ? invoice.invoice_number.formatted_number
            : invoice.invoice_no || 'No number');

        // Use multiple fields for customerName with fallbacks
        const customerName = invoice.customer_name ||
          invoice.company?.name ||
          invoice.company_name ||
          'Unknown';

        // Use store_type_id or store_type with proper fallbacks
        let storeTypeValue: string = 'Unknown';
        if (invoice.store_type && typeof invoice.store_type === 'object' && invoice.store_type.name) {
          storeTypeValue = invoice.store_type.name;
        } else if (invoice.store_type_name) {
          storeTypeValue = invoice.store_type_name;
        } else if (typeof invoice.store_type === 'string') {
          storeTypeValue = invoice.store_type;
        } else if (invoice.store_type_id !== undefined) {
          // Map store type IDs to names
          if (invoice.store_type_id === 1) {
            storeTypeValue = 'Cranbourne';
          } else if (invoice.store_type_id === 2) {
            storeTypeValue = 'Sale';
          } else {
            storeTypeValue = `Store ${invoice.store_type_id}`;
          }
        }

        // Create the base invoice object
        const mappedInvoice: Invoice = {
          id: invoice.id,
          invoiceNo,  // Use the resolved invoice number
          customerName,  // Use the resolved customer name
          storeType: storeTypeValue,  // Always a string
          date: invoice.date ? format(parseISO(invoice.date), 'dd/MM/yyyy') : 'Unknown',
          total, // This is now safely a string formatted with 2 decimal places
          notes: invoice.notes,
          hasNotes: !!invoice.notes && invoice.notes.trim() !== '',
          status: invoice.status
        };

        // Add optional properties only if they exist in the source
        if ('deliver_to_address' in invoice) {
          mappedInvoice.deliverToAddress = (invoice as any).deliver_to_address || '';
        }
        
        if ('purchase_order_number' in invoice) {
          mappedInvoice.poNumber = (invoice as any).purchase_order_number || '';
        }

        return mappedInvoice;
      });

      setInvoices(mappedInvoices);
      setTotalItems(data.total || mappedInvoices.length);

    } catch (err) {
      // Handle rate limit errors gracefully
      if (err && typeof err === 'object' && 'message' in err) {
        const errorObj = err as { message: string, status?: number };

        if (errorObj.message.includes('debounce') || errorObj.status === 429) {
          setError("Loading data, please wait");
          
          // Retry after a short delay for rate limits (don't bypass cache on retry)
          setTimeout(() => {
            fetchInvoices(page, perPage, queryFilters, false);
          }, 2000);
          return;
        } else {
          setError("Failed to load invoices. " + errorObj.message);
        }
      } else {
        setError("Failed to load invoices. Please try again later.");
      }

      setInvoices([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [storeFilter]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Track navigation to the invoices page to force fresh fetch
  useEffect(() => {
    // Check if user navigated to the invoices page (pathname changed to /invoices)
    const isNavigatingToPage = lastPathnameRef.current !== location.pathname && location.pathname === '/invoices';
    
    if (isNavigatingToPage) {
      // User navigated to invoices page, force fresh fetch
      isInitialLoadRef.current = true;
      lastPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Simplified data fetching effect - only fetch when parameters change
  useEffect(() => {
    // Only fetch data when in LIST view
    if (currentView === PageView.LIST) {
      // Force fresh fetch on initial load or when navigating to the page
      const shouldBypassCache = isInitialLoadRef.current;
      
      // Reset the flag after first use
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }
      
      fetchInvoices(currentPage, itemsPerPage, memoizedFilters, shouldBypassCache);
    }
  }, [currentPage, itemsPerPage, memoizedFilters, currentView, location.pathname]);

  // Handle search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setSearchQuery(searchValue);

    // Use debounced search to avoid too many API calls
    debouncedSearch(searchValue);
  };

  // Handle date range changes
  const handleDateRangeChange = (newDateRange: string) => {
    setSelectedDateRange(newDateRange);

    // Parse the date range and update filters
    if (newDateRange) {
      const [startStr, endStr] = newDateRange.split(' - ');

      try {
        // Parse from DD/MM/YYYY format
        const [startDay, startMonth, startYear] = startStr.split('/').map(Number);
        const [endDay, endMonth, endYear] = endStr.split('/').map(Number);

        const startDate = new Date(startYear, startMonth - 1, startDay);
        const endDate = new Date(endYear, endMonth - 1, endDay);

        // Validate date range (30 days)
        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 30) {
          // If range is more than 30 days, adjust start date to be 30 days before end date
          startDate.setDate(endDate.getDate() - 30);
        }

        // Format dates for backend (YYYY-MM-DD)
        const formattedStartDate = formatDateForBackend(startDate);
        const formattedEndDate = formatDateForBackend(endDate);

        setFilters({
          ...filters,
          start_date: formattedStartDate,
          end_date: formattedEndDate
        });
      } catch (err) {
        // Error parsing date range
      }
    } else {
      // Clear date filters
      const { start_date, end_date, ...restFilters } = filters;
      setFilters(restFilters);
    }
  };

  // Pagination handlers
  const handlePageChange = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // View handlers
  const handleCreateInvoice = () => {
    // Navigate to the create invoice view using URL parameter
    // This way we avoid direct state manipulation which can lead to race conditions
    navigate('?create=true', { replace: true });
  };

  const handleEditInvoice = (invoiceId: string) => {
    // Navigate to the edit invoice view using URL parameter
    // The useEffect will automatically handle fetching the invoice data
    navigate(`/invoices?edit=${invoiceId}`, { replace: true });
  };

  const handleEditInvoiceForm = useCallback(async (invoiceId: string) => {
    setIsLoading(true);
    try {
      console.log("Attempting to fetch invoice data for ID:", invoiceId);
      
      // Always try to fetch the full invoice data from the API first
      try {
        console.log("Making API call to getInvoice with ID:", invoiceId);
        const invoiceData = await getInvoice(invoiceId);
        console.log("Successfully fetched invoice data:", invoiceData);

        // Set the current invoice data
        setCurrentInvoice(invoiceData);
        setSelectedInvoiceId(invoiceId);

        return;
      } catch (apiError) {
        console.error("Error fetching invoice data:", apiError);
        
        // Get more detailed error information
        let errorMessage = "Unknown error";
        if (apiError instanceof Error) {
          errorMessage = apiError.message;
        } else if (typeof apiError === 'object' && apiError !== null) {
          errorMessage = JSON.stringify(apiError);
        }
        console.error("Detailed error message:", errorMessage);
        
        // Fallback: Check if we're dealing with a numeric ID and use existing data
        const isNumericId = /^\d+$/.test(invoiceId);

        if (isNumericId) {
          console.log("Using fallback mechanism for numeric ID:", invoiceId);
          // Find the invoice in our already loaded list
          const existingInvoice = invoices.find(inv => inv.id === invoiceId);
          if (existingInvoice) {
            console.log("Found existing invoice in local data:", existingInvoice);
            // Create a more complete InvoiceOut object with available data
            const fallbackInvoiceData: InvoiceOut = {
              id: existingInvoice.id,
              store_type: {
                id: existingInvoice.storeType ? 
                  parseInt(existingInvoice.storeType) || 1 : 1, // Try to parse store type ID if available
                name: existingInvoice.storeType || 'Cranbourne'
              },
              store_type_id: existingInvoice.storeType ? 
                parseInt(existingInvoice.storeType) || 1 : 1, // Try to parse store type ID if available
              sale_type: SaleTypeEnum.TRADE, // Default, user can change
              mode_of_payment: PaymentModeEnum.CASH, // Default to cash as requested
              customer_id: existingInvoice.customerName ? existingInvoice.customerName : '', // Will need to be selected by user
              date: existingInvoice.date || new Date().toISOString().split('T')[0],
              dont_send_po: false,
              shipping: 0,
              notes: existingInvoice.notes || '',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              items: [{
                id: 'fallback-item-1',
                sku: '',
                description: '',
                units: 0,
                boxes: 0,
                pieces: 0,
                m2: 0,
                blm: 0,
                unit_price: 0,
                invoice_id: existingInvoice.id,
                total_price: 0,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                deduct_from_stock: false // Default to false as requested
              }], // Start with one empty item with deduct_from_stock set to false
              // Add required fields with better defaults
              total_order: parseFloat(existingInvoice.total?.replace(/[^\d.-]/g, '') || '0') || 0,
              credit_card_surcharge: '0',
              total_gst: '0',
              grand_total: existingInvoice.total || '0',
              // Add invoice number data
              invoice_number: {
                id: existingInvoice.id,
                formatted_number: existingInvoice.invoiceNo || ''
              },
              // Add company info if available
              company: existingInvoice.customerName ? {
                id: '',
                name: existingInvoice.customerName
              } : undefined,
              // Add delivery address placeholder
              deliver_to_address: existingInvoice.deliverToAddress || 'Melbourne', // Use existing delivery address or default to Melbourne
              // Add purchase order number placeholder
              purchase_order_number: existingInvoice.poNumber || ''
            };

            console.log("Created fallback invoice data:", fallbackInvoiceData);
            // Set the current invoice data
            setCurrentInvoice(fallbackInvoiceData);
            setSelectedInvoiceId(invoiceId);

            toast.showToast("Note: Some invoice details may need to be re-entered as they couldn't be fully loaded.", { type: 'info' });
            return;
          } else {
            console.error("Could not find invoice in local data with ID:", invoiceId);
          }
        }

        // If we couldn't find invoice in existing list or it's not numeric
        toast.showToast("Could not load this invoice for editing. Please refresh the page and try again.", { type: 'error' });
        return;
      }
    } catch (err) {
      console.error("Unexpected error in handleEditInvoiceForm:", err);
      toast.showToast("Failed to load invoice details. Please try again later.", { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, []); // Remove invoices and toast from dependencies to prevent infinite loops

  const handleCloseInvoice = () => {
    // Check for returnTo parameter in URL
    const urlParams = new URLSearchParams(location.search);
    const returnTo = urlParams.get('returnTo');
    
    // Check if form has unsaved changes
    if (hasUnsavedChanges && (currentView === PageView.CREATE || currentView === PageView.EDIT)) {
      setPendingNavigation(() => () => {
        // Navigate back to returnTo if present, otherwise to list view
        if (returnTo) {
          navigate(decodeURIComponent(returnTo), { replace: true });
        } else {
          navigate('/invoices', { replace: true });
        }

        // Reset view state
        setCurrentView(PageView.LIST);
        setSelectedInvoiceId(null);
        setCurrentInvoice(null);

        // Clear any errors
        setError(null);
        setHasUnsavedChanges(false);
      });
      setShowExitConfirmation(true);
    } else {
      // Navigate back to returnTo if present, otherwise to list view
      if (returnTo) {
        navigate(decodeURIComponent(returnTo), { replace: true });
      } else {
        navigate('/invoices', { replace: true });
      }

      // Reset view state
      setCurrentView(PageView.LIST);
      setSelectedInvoiceId(null);
      setCurrentInvoice(null);

      // Clear any errors
      setError(null);
      setHasUnsavedChanges(false);
    }
  };

  const handleConfirmExit = () => {
    setShowExitConfirmation(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const handleCancelExit = () => {
    setShowExitConfirmation(false);
    setPendingNavigation(null);
  };

  // Notes modal handlers
  const handleViewNotes = async (invoiceId: string) => {
    setIsLoading(true);
    try {
      // First check if we're dealing with a numeric ID
      const isNumericId = /^\d+$/.test(invoiceId);

      if (isNumericId) {
        // For numeric IDs, don't try to load the full invoice
        // Just use a special case for notes viewing

        // Find the invoice in our already loaded list
        const existingInvoice = invoices.find(inv => inv.id === invoiceId);
        if (existingInvoice) {
          // Use the notes we already have
          setNotesValue(existingInvoice.notes || '');
          setNotesInvoiceId(invoiceId);
          setShowNotesModal(true);
          return;
        }

        // If we couldn't find it in existing invoices, show an error
        setError("Cannot load notes for this invoice. Try refreshing the page.");
        return;
      }

      // For UUID IDs, proceed normally
      const invoiceData = await getInvoice(invoiceId);
      setNotesValue(invoiceData?.notes || '');
      setNotesInvoiceId(invoiceId);
      setShowNotesModal(true);
    } catch (err) {
      setError("Failed to load invoice notes. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!notesInvoiceId) {
      return;
    }

    setIsSavingNotes(true);
    try {
      await updateInvoice(notesInvoiceId, { notes: notesValue });

      // Update the notes in the local state
      setInvoices(invoices.map(inv =>
        inv.id === notesInvoiceId
          ? { ...inv, notes: notesValue, hasNotes: !!notesValue && notesValue.trim() !== '' }
          : inv
      ));

      setShowNotesModal(false);
      toast.showToast("Notes saved successfully!", { type: 'success' });
    } catch (err) {
      toast.showToast("Failed to save notes. Please try again.", { type: 'error' });
    } finally {
      setIsSavingNotes(false);
    }
  };

  // API action handlers
  const handleSubmitInvoice = async (data: InvoiceData) => {
    setIsLoading(true);
    try {
      // Helper function to round to 2 decimal places
      const roundToTwoDecimal = (value: any): string => {
        if (value === null || value === undefined || value === '') return '0';
        const num = typeof value === 'string' ? parseFloat(value) : Number(value);
        return isNaN(num) ? '0' : (Math.round((num + Number.EPSILON) * 100) / 100).toFixed(2);
      };

      // Convert form data to API data
      const apiData: any = {
        store_type_id: typeof data.storeType === 'number' ? data.storeType : undefined,
        store_type: typeof data.storeType === 'string' ? {
          name: data.storeType
        } : undefined,
        sale_type: (data.saleType && data.saleType.toLowerCase() === 'retail')
          ? SaleTypeEnum.RETAIL
          : (data.saleType && data.saleType.toLowerCase() === 'store')
            ? SaleTypeEnum.STORE
            : SaleTypeEnum.TRADE,
        mode_of_payment: (data.modeOfPayment && data.modeOfPayment.toLowerCase() === 'credit card')
          ? PaymentModeEnum.CREDIT_CARD
          : (data.modeOfPayment && data.modeOfPayment.toLowerCase() === 'bank transfer')
            ? PaymentModeEnum.BANK_TRANSFER
            : PaymentModeEnum.CASH,
        purchase_order_number: data.poNumber || null,
        customer_id: data.invoiceTo ? String(data.invoiceTo) : undefined,
        deliver_to_address: data.deliverToAddress || null,
        date: data.date,
        dont_send_po: data.dontSendPo,
        notes: data.notes || '',
        status: "saved",  // Explicitly set status to saved
        shipping: roundToTwoDecimal(data.totals.shipping || 0),
        total_order: roundToTwoDecimal(data.totals.orderTotal || 0),
        credit_card_surcharge: roundToTwoDecimal(data.totals.surcharge || 0),
        total_gst: roundToTwoDecimal(data.totals.gst || 0),
        grand_total: roundToTwoDecimal(data.totals.grandTotal || 0),
        discount: roundToTwoDecimal(data.totals.discount || 0), // Include discount in API data
        items: data.items.filter(item => {
          return (
            (item.quantity.units || 0) > 0 ||
            (item.quantity.boxes || 0) > 0 ||
            (item.quantity.pieces || 0) > 0 ||
            (item.quantity.m2 || 0) > 0
          ) && item.sku && item.description;
        }).map(item => ({
          sku: item.sku,
          description: item.description,
          units: item.quantity.units || 0,
          boxes: item.quantity.boxes || 0,
          pieces: item.quantity.pieces || 0,
          m2: item.quantity.m2 || 0,
          unit_price: roundToTwoDecimal(item.unitPrice),
          deduct_from_stock: item.deductFromStock
        }))
      };

      // Add quote ID if valid
      const processedQuoteId = getQuoteIdForApi(data.linkedQuote);
      if (processedQuoteId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(processedQuoteId)) {
        apiData.linked_quote_id = processedQuoteId;
      }

      let result;

      // Determine if we should create or update based on the current view and selected invoice
      if (currentView === PageView.CREATE) {
        // This is a new invoice
        console.log('🆕 Creating new invoice');
        console.log('API Data:', JSON.stringify(apiData, null, 2));
        
        try {
          const response = await fetch(`${API_URL}${ENDPOINTS.INVOICES}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}`
            },
            body: JSON.stringify(apiData)
          });

          if (!response.ok) {
            const errorText = await response.text();
            let errorMessage = `Failed to create invoice: ${response.status} ${response.statusText}`;
            
            // Check for specific 422 status (validation error) and future date case
            if (response.status === 422) {
              // Check if the invoice date is in the future
              const invoiceDate = new Date(data.date);
              const today = new Date();
              today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
              
              if (invoiceDate > today) {
                // This is a future-dated invoice attempting to be saved as non-draft
                toast.showToast("Future Dated Invoice can only be Saved as Draft", { type: 'error' });
                return; // Exit early, don't continue with normal error handling
              }
            }
            
            // Try to parse the error response for more specific messages
            try {
              const errorData = JSON.parse(errorText);
              if (errorData.detail && typeof errorData.detail === 'string') {
                errorMessage = errorData.detail;
              }
            } catch (parseError) {
              // If parsing fails, include the raw error text
              if (errorText) {
                errorMessage += ` - ${errorText}`;
              }
            }
            
            throw new Error(errorMessage);
          }

          result = await response.json();
          console.log('Create invoice response:', result);
          
          // Show success toast
          toast.showToast('New Invoice Created Successfully', { type: 'success' });
        } catch (createError) {
          // ... existing error handling code ...
        }
      } else if (currentView === PageView.EDIT && selectedInvoiceId) {
        // This is an edit of an existing invoice (both numeric and UUID IDs supported)
        console.log(`🔄 Updating existing invoice: ${selectedInvoiceId}`);
        console.log('API Data:', JSON.stringify(apiData, null, 2));

        try {
          // Update the invoice using the PUT endpoint
          const response = await fetch(`${API_URL}${ENDPOINTS.INVOICES}/${selectedInvoiceId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}`
            },
            body: JSON.stringify(apiData)
          });

          if (!response.ok) {
            const errorText = await response.text();

            // Handle specific error cases
            if (response.status === 404) {
              throw new Error(`Invoice with ID ${selectedInvoiceId} was not found. It may have been deleted or the ID is invalid.`);
            } else if (response.status === 422) {
              // Check if the invoice date is in the future
              const invoiceDate = new Date(data.date);
              const today = new Date();
              today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
              
              if (invoiceDate > today) {
                // This is a future-dated invoice attempting to be saved as non-draft
                toast.showToast("Future Dated Invoice can only be Saved as Draft", { type: 'error' });
                return; // Exit early, don't continue with normal error handling
              }
              throw new Error(`Failed to update invoice: ${response.status} ${response.statusText}`);
            } else {
              throw new Error(`Failed to update invoice: ${response.status} ${response.statusText}`);
            }
          }

          result = await response.json();
          console.log('Update invoice response:', result);
          
          // Show success toast for update
          toast.showToast('Invoice Updated Successfully', { type: 'success' });
        } catch (updateError) {
          // ... existing error handling code ...
        }
      } else {
        throw new Error('Invalid operation: Unable to determine whether to create or update invoice');
      }

      // Force immediate data refresh
      
      // Switch back to list view first
      setCurrentView(PageView.LIST);
      setCurrentInvoice(null);
      setSelectedInvoiceId(null);

      // Navigate to clear URL parameters and trigger refresh
      navigate('/invoices', { replace: true, state: { refresh: true } });

    } catch (err) {
      console.error('Invoice submission error:', err);
      
      // Extract error message
      let errorMessage = `Failed to ${currentView === PageView.CREATE ? 'create' : 'update'} invoice. Please try again later.`;
      
      if (err instanceof Error) {
        // Check if this is a stock validation error
        if (err.message.includes('Insufficient inventory stock') || err.message.includes('Required') || err.message.includes('Available')) {
          errorMessage = err.message;
        } else if (err.message.length > 0 && err.message.length < 200) {
          // Use the specific error message if it's reasonable length
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      // Show error toast
      toast.showToast(errorMessage, { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle save as draft
  const handleSaveAsDraft = async (data: InvoiceData) => {
    // Save as draft should bypass validation
    setIsLoading(true);
    try {
      // Helper function to round to 2 decimal places
      const roundToTwoDecimal = (value: any): string => {
        if (value === null || value === undefined || value === '') return '0';
        const num = typeof value === 'string' ? parseFloat(value) : Number(value);
        return isNaN(num) ? '0' : (Math.round((num + Number.EPSILON) * 100) / 100).toFixed(2);
      };

      // Convert form data to API data with draft status
      const apiData: any = {
        store_type_id: typeof data.storeType === 'number' ? data.storeType : undefined,
        store_type: typeof data.storeType === 'string' ? {
          name: data.storeType || 'draft_type'
        } : undefined,
        // Convert strings to proper enum values
        sale_type: (data.saleType && data.saleType.toLowerCase() === 'retail')
          ? SaleTypeEnum.RETAIL
          : (data.saleType && data.saleType.toLowerCase() === 'store')
            ? SaleTypeEnum.STORE
            : SaleTypeEnum.TRADE,

        // Convert string values to proper enum values
        mode_of_payment: (data.modeOfPayment && data.modeOfPayment.toLowerCase() === 'credit card')
          ? PaymentModeEnum.CREDIT_CARD
          : (data.modeOfPayment && data.modeOfPayment.toLowerCase() === 'bank transfer')
            ? PaymentModeEnum.BANK_TRANSFER
            : PaymentModeEnum.CASH,

        purchase_order_number: data.poNumber || 'Draft',
        // Always use a valid customer ID - even for drafts
        customer_id: data.invoiceTo ? String(data.invoiceTo) : "00000000-0000-0000-0000-000000000001", // Valid dummy UUID
        deliver_to_address: data.deliverToAddress || 'Draft',
        date: data.date || new Date().toISOString().split('T')[0],
        dont_send_po: data.dontSendPo,
        notes: data.notes || '',
        status: "draft",  // Explicitly set status to draft
        shipping: roundToTwoDecimal(data.totals.shipping || 0),
        total_order: roundToTwoDecimal(data.totals.orderTotal || 0),
        credit_card_surcharge: roundToTwoDecimal(data.totals.surcharge || 0),
        total_gst: roundToTwoDecimal(data.totals.gst || 0),
        grand_total: roundToTwoDecimal(data.totals.grandTotal || 0),
        discount: roundToTwoDecimal(data.totals.discount || 0), // Include discount in API data
        items: data.items.length > 0 ? data.items.map(item => ({
          sku: item.sku || 'DRAFT-SKU',
          description: item.description || 'Draft Item',
          units: parseFloat(String(item.quantity.units || 0)),
          boxes: parseFloat(String(item.quantity.boxes || 0)),
          pieces: parseFloat(String(item.quantity.pieces || 0)),
          m2: parseFloat(String(item.quantity.m2 || 0)),
          unit_price: roundToTwoDecimal(item.unitPrice || 0),
          deduct_from_stock: Boolean(item.deductFromStock)
        })) : [
          // Include at least one dummy item if no items yet
          {
            sku: 'DRAFT-SKU',
            description: 'Draft Item',
            units: 1,
            boxes: 0,
            pieces: 0,
            m2: 0,
            unit_price: '0',
            deduct_from_stock: false
          }
        ]
      };

      // Add quote ID if valid
      const processedQuoteId = getQuoteIdForApi(data.linkedQuote);
      if (processedQuoteId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(processedQuoteId)) {
        apiData.linked_quote_id = processedQuoteId;
      }

      let result;

      // Check if this is a new draft or updating an existing one
      // If we're in EDIT view and have a selectedInvoiceId, we should UPDATE the existing invoice
      // Only CREATE if we're in CREATE view or don't have a selectedInvoiceId
      if (currentView === PageView.CREATE || !selectedInvoiceId) {
        // Create a new draft invoice
        console.log('🆕 Creating new draft invoice');
        console.log('API Data:', JSON.stringify(apiData, null, 2));
        const token = localStorage.getItem(AUTH_TOKEN_KEY);

        if (!token) {
          throw new Error("Authentication token not found");
        }

        try {
          const response = await fetch(`${API_URL}${ENDPOINTS.INVOICES}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(apiData)
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to create draft invoice: ${response.status} ${response.statusText} - ${errorText}`);
          }

          result = await response.json();
          console.log('Create draft invoice response:', result);
          
          // Show success toast
          toast.showToast('Draft Saved Successfully', { type: 'success' });
        } catch (createError) {
          // ... existing error handling code ...
        }
      } else if (selectedInvoiceId) {
        // Update existing draft invoice
        console.log(`🔄 Updating existing draft invoice: ${selectedInvoiceId}`);
        console.log('API Data:', JSON.stringify(apiData, null, 2));
        const token = localStorage.getItem(AUTH_TOKEN_KEY);

        if (!token) {
          throw new Error("Authentication token not found");
        }

        const response = await fetch(`${API_URL}${ENDPOINTS.INVOICES}/${selectedInvoiceId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(apiData)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to update draft invoice: ${response.status} ${response.statusText} - ${errorText}`);
        }

        result = await response.json();
        console.log('Update draft invoice response:', result);
        
        // Show success toast for update
        toast.showToast('Draft Updated Successfully', { type: 'success' });
      }

      // Force immediate data refresh
      
      // Switch back to list view first
      setCurrentView(PageView.LIST);
      setCurrentInvoice(null);
      setSelectedInvoiceId(null);

      // Navigate to clear URL parameters and trigger refresh
      navigate('/invoices', { replace: true, state: { refresh: true } });

    } catch (err) {
      setError("Failed to save draft. Please try again.");
      // Show error toast
      toast.showToast("Failed to save draft. Please try again.", { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to prepare form data from invoice
  const prepareFormDataFromInvoice = useCallback((invoice: InvoiceOut): InvoiceData => {
    console.log('Preparing form data from invoice:', invoice);

    // Map the API invoice data to the form data format
    return {
      id: invoice.id,
      storeType: invoice.store_type?.name || 
                (invoice as any).store_type_name || // Prioritize store_type_name (preserves quote's store type)
                (invoice.store_type as any)?.store_type_name || // Use type assertion for backward compatibility
                (typeof invoice.store_type === 'string' ? invoice.store_type : null) ||
                (invoice as any).invoice_type || 
                'Cranbourne',
      saleType: typeof invoice.sale_type === 'string' && /^\d+$/.test(invoice.sale_type) ? 
               ((invoice.sale_type as string) === '1' ? 'Trade' : 
                (invoice.sale_type as string) === '2' ? 'Retail' : 
                (invoice.sale_type as string) === '3' ? 'Store' : 'Trade') :
               (invoice.sale_type as any) === SaleTypeEnum.RETAIL ? 'Retail' : 
               (invoice.sale_type as any) === SaleTypeEnum.STORE ? 'Store' : 'Trade',
      modeOfPayment: (() => {
        const payment = invoice.mode_of_payment as any; // Use any to handle different possible types
        // Handle numeric strings
        if (typeof payment === 'string' && /^\d+$/.test(payment)) {
          return payment === '1' ? 'Cash' :
                 payment === '2' ? 'Bank Transfer' :
                 payment === '3' ? 'Credit Card' : 'Cash';
        }
        // Handle literal string values from API
        if (typeof payment === 'string') {
          const lowerPayment = payment.toLowerCase();
          if (lowerPayment === 'cash') return 'Cash';
          if (lowerPayment === 'bank transfer' || lowerPayment === 'bank_transfer') return 'Bank Transfer';
          if (lowerPayment === 'credit card' || lowerPayment === 'credit_card') return 'Credit Card';
        }
        // Handle enum values
        if (payment === PaymentModeEnum.CREDIT_CARD) return 'Credit Card';
        if (payment === PaymentModeEnum.BANK_TRANSFER) return 'Bank Transfer';
        // Default fallback
        return 'Cash';
      })(),
      poNumber: invoice.purchase_order_number || '',
      invoiceTo: invoice.customer_id || '',
      deliverToAddress: invoice.deliver_to_address || '',
      invoiceNo: invoice.invoice_number?.formatted_number || invoice.invoice_no || '',
      notes: invoice.notes || '',
      linkedQuote: invoice.linked_quote_id || '',
      date: typeof invoice.date === 'string' ? invoice.date.split('T')[0] : 
           new Date().toISOString().split('T')[0],
      dontSendPo: invoice.dont_send_po || false,
      status: invoice.status || 'draft',
      items: (invoice.items || []).map((item: any) => {
        // Handle both array of items and nested items object
        const itemData = item.sku ? item : (typeof item === 'object' && ('sku' in item || 'sku_code' in item) ? item : {});
        
        return {
          id: itemData.id || String(Math.random()),
          sku: itemData.sku || itemData.sku_code || '',
          description: itemData.description || '',
          quantity: {
            units: parseFloat(itemData.units || itemData.quantity_units || 0),
            boxes: parseFloat(itemData.boxes || itemData.quantity_boxes || 0),
            pieces: parseFloat(itemData.pieces || itemData.quantity_pieces || 0),
            m2: parseFloat(itemData.m2 || itemData.quantity_m2 || 0),
            blm: parseFloat(itemData.blm || itemData.quantity_blm || 0),
            total: parseFloat(itemData.units || itemData.quantity_units || 0) +
                  parseFloat(itemData.boxes || itemData.quantity_boxes || 0) +
                  parseFloat(itemData.pieces || itemData.quantity_pieces || 0) +
                  parseFloat(itemData.m2 || itemData.quantity_m2 || 0)
                  // BLM is excluded from total calculation as per requirements
          },
          unitPrice: parseFloat(itemData.unit_price || 0),
          totalPrice: parseFloat(itemData.total_price || itemData.totalPrice || 0),
          deductFromStock: Boolean(itemData.deduct_from_stock)
        };
      }),
      totals: {
        orderTotal: typeof invoice.total_order === 'string' ? 
                   parseFloat(invoice.total_order) || 0 : 
                   (invoice.total_order || 0),
        surcharge: typeof invoice.credit_card_surcharge === 'string' ? 
                  parseFloat(invoice.credit_card_surcharge) || 0 : 
                  (invoice.credit_card_surcharge || 0),
        gst: typeof invoice.total_gst === 'string' ? 
            parseFloat(invoice.total_gst) || 0 : 
            (invoice.total_gst || 0),
        discount: typeof invoice.discount === 'string' ? 
                 parseFloat(invoice.discount) || 0 : 
                 (invoice.discount || 0),
        shipping: typeof invoice.shipping === 'string' ? 
                 parseFloat(invoice.shipping) || 0 : 
                 (invoice.shipping || 0),
        grandTotal: typeof invoice.grand_total === 'string' ? 
                   parseFloat(invoice.grand_total) || 0 : 
                   (invoice.grand_total || 0)
      }
    };
  }, []); // No dependencies needed as this is a pure function

  // Download invoice handler - opens InvoiceView modal for PDF download
  const handleDownloadInvoice = async (invoiceId: string) => {
    // Close any existing modal first to ensure clean state
    if (isViewInvoiceOpen) {
      setIsViewInvoiceOpen(false);
      setViewInvoiceId(null);

      // Small delay to ensure the previous modal is fully unmounted
      setTimeout(() => {
        setViewInvoiceId(invoiceId);
        setIsViewInvoiceOpen(true);
      }, 100);
    } else {
      setViewInvoiceId(invoiceId);
      setIsViewInvoiceOpen(true);
    }
  };

  // Function to close the invoice view modal
  const handleCloseInvoiceView = () => {
    setIsViewInvoiceOpen(false);
    setViewInvoiceId(null);
  };

  // Helper function to extract and sanitize error messages
  const extractErrorMessage = (err: any): string => {
    const MAX_MESSAGE_LENGTH = 150; // Maximum characters for toast message
    const DEFAULT_MESSAGE = "Failed to send email. Please try again later.";
    
    let errorMessage = '';
    
    // Try to extract error message from different error structures
    // Priority: response.data.detail (raw API) > Error.message > other sources
    if (err && typeof err === 'object' && 'response' in err && err.response?.data) {
      // Try to get the raw API response first (before apiClient wraps it)
      if (err.response.data.detail) {
        errorMessage = typeof err.response.data.detail === 'string' 
          ? err.response.data.detail 
          : String(err.response.data.detail);
      } else if (err.response.data.message) {
        errorMessage = err.response.data.message;
      }
    }
    
    // If we didn't get it from response, try Error.message
    if (!errorMessage && err instanceof Error) {
      errorMessage = err.message || '';
    } else if (!errorMessage && err && typeof err === 'object') {
      if ('detail' in err && typeof err.detail === 'string') {
        errorMessage = err.detail;
      } else if ('message' in err && typeof err.message === 'string') {
        errorMessage = err.message;
      }
    } else if (!errorMessage && typeof err === 'string') {
      errorMessage = err;
    }
    
    // EARLY DETECTION: Check for network/connection errors first - return simple message immediately
    // This prevents showing technical jargon to users
    if (errorMessage) {
      const lowerMessage = errorMessage.toLowerCase();
      
      // Network/connection errors - show simple message
      const isNetworkError = 
        lowerMessage.includes('unable to connect') ||
        lowerMessage.includes('request failed') ||
        lowerMessage.includes('failed to fetch') ||
        lowerMessage.includes('network error') ||
        lowerMessage.includes('connection refused') ||
        lowerMessage.includes('connection timeout') ||
        lowerMessage.includes('server is temporarily unavailable') ||
        lowerMessage.includes('network connection issues') ||
        lowerMessage.includes('firewall or proxy') ||
        lowerMessage.includes('check your internet connection') ||
        lowerMessage.includes('no internet connection') ||
        lowerMessage.includes('server connection refused') ||
        lowerMessage.includes('server address could not be resolved') ||
        lowerMessage.includes('cross-origin request blocked') ||
        lowerMessage.includes('request timed out') ||
        lowerMessage.includes('network connection changed');
      
      if (isNetworkError) {
        return DEFAULT_MESSAGE;
      }
      
      // Check for specific user-friendly messages (only for API responses, not network errors)
      if (lowerMessage.includes('customer has no email address')) {
        return 'Customer has no email address';
      }
    }
    
    // Sanitize the error message - remove all technical prefixes and wrappers
    if (errorMessage && errorMessage.trim()) {
      // Remove stack traces and technical details
      errorMessage = errorMessage
        .split('\n')[0] // Take only the first line
        .split('at ')[0] // Remove stack trace indicators
        .trim();
      
      // Remove all technical prefixes and server error wrappers
      // Handle patterns like: "Server error occurred. An internal server error occurred. Details: Failed to send email..."
      errorMessage = errorMessage
        .replace(/Server error occurred\.\s*/gi, '')
        .replace(/An internal server error occurred\.\s*/gi, '')
        .replace(/Bad gateway[^.]*\.\s*/gi, '')
        .replace(/Service unavailable[^.]*\.\s*/gi, '')
        .replace(/Gateway timeout[^.]*\.\s*/gi, '')
        .replace(/Server returned error \d+\.\s*/gi, '')
        .replace(/Details:\s*/gi, '')
        .replace(/Please try again later or contact support[^.]*\./gi, '')
        .replace(/^(Error|TypeError|ReferenceError|SyntaxError):\s*/i, '')
        .replace(/^API Error:\s*/i, '')
        .replace(/^HTTP \d+:\s*/i, '')
        .replace(/^Request failed:\s*/i, '')
        .trim();
      
      // Check for specific user-friendly messages (after cleaning)
      const lowerMessage = errorMessage.toLowerCase();
      if (lowerMessage.includes('customer has no email address')) {
        return 'Customer has no email address';
      }
      
      // If message contains "Failed to send email" (in any form), use simple default
      if (lowerMessage.includes('failed to send email') || 
          lowerMessage.includes('failed to email')) {
        return DEFAULT_MESSAGE;
      }
      
      // Check if the message still contains server error jargon after cleaning
      const hasServerErrorJargon = /server error|internal server|bad gateway|service unavailable|gateway timeout/i.test(errorMessage);
      const isTechnicalNoise = /^(fetch|network|timeout|abort|cors|failed to fetch|unable to connect|request failed)/i.test(errorMessage);
      const isTooShort = errorMessage.length < 3;
      const isTooLong = errorMessage.length > 500;
      
      // If it's technical noise or contains server error jargon, use simple default
      if (isTechnicalNoise || hasServerErrorJargon) {
        return DEFAULT_MESSAGE;
      }
      
      if (!isTooShort && !isTooLong) {
        // Truncate if too long for display
        if (errorMessage.length > MAX_MESSAGE_LENGTH) {
          errorMessage = errorMessage.substring(0, MAX_MESSAGE_LENGTH - 3) + '...';
        }
        // Return the sanitized API error message (e.g., "Customer has no email address")
        return errorMessage;
      }
    }
    
    // Fallback to default message for network errors, timeouts, or unparseable errors
    return DEFAULT_MESSAGE;
  };

  // Email invoice handler - updated with better error handling
  const handleEmailInvoice = async (invoiceId: string) => {
    console.log("=== EMAIL INVOICE DEBUG START ===");
    console.log("Invoice ID:", invoiceId);
    console.log("Invoice ID type:", typeof invoiceId);
    console.log("Is loading:", isLoading);
    
    // Email loading state is now tracked by emailingInvoiceId
    setEmailingInvoiceId(invoiceId);
    try {
      console.log("Proceeding with email for invoice ID:", invoiceId);
      console.log("About to call emailInvoice function...");
      
      // Proceed with email for any valid invoice ID
      const result = await emailInvoice(invoiceId);
      console.log("Email function completed successfully:", result);
      
      toast.showToast("Email - Successfully sent", { type: 'success' });
    } catch (err) {
      console.error("Error in handleEmailInvoice:", err);
      console.error("Error type:", typeof err);
      console.error("Error constructor:", err?.constructor?.name);
      
      if (err instanceof Error) {
        console.error("Error message:", err.message);
        console.error("Error stack:", err.stack);
      }
      
      // Extract and sanitize the error message with safeguards
      const errorMessage = extractErrorMessage(err);
      toast.showToast(errorMessage, { type: 'error' });
    } finally {
      console.log("Setting isLoading to false");
      // Email loading state cleanup handled by setEmailingInvoiceId(null)
      setEmailingInvoiceId(null);
      console.log("=== EMAIL INVOICE DEBUG END ===");
    }
  };

  // Delete draft invoice handler
  const handleDeleteInvoice = (invoiceId: string, invoiceNo: string) => {
    setInvoiceToDelete({ id: invoiceId, invoiceNo });
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    
    setIsLoading(true);
    setShowDeleteConfirmation(false);
    try {
      await deleteInvoice(invoiceToDelete.id);
      toast.showToast("Invoice deleted successfully", { type: 'success' });
      // Refresh the list of invoices
      fetchInvoices(currentPage, itemsPerPage, filters, true);
    } catch (err) {
      console.error("Error in handleDeleteInvoice:", err);
      const errorMessage = extractErrorMessage(err);
      toast.showToast(errorMessage || "Failed to delete invoice", { type: 'error' });
    } finally {
      setIsLoading(false);
      setInvoiceToDelete(null);
    }
  };

  const handleBack = () => {
    // Navigate back to the home page
    navigate('/home');
  };

  // Initialize view state based on URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    
    if (urlParams.get('create') === 'true') {
      setCurrentView(PageView.CREATE);
      setEditingInvoiceId(null);
      setCurrentInvoice(null);
    } else if (urlParams.get('edit')) {
      const editId = urlParams.get('edit');
      if (editId) {
        setEditingInvoiceId(editId);
        setCurrentView(PageView.EDIT);
      }
    } else {
      setCurrentView(PageView.LIST);
      setEditingInvoiceId(null);
      setCurrentInvoice(null);
    }
  }, [location.search]);

  // Optimized refresh effect for navigation from create/edit pages
  useEffect(() => {
    const shouldRefresh = location.state?.refresh;
    
    if (shouldRefresh) {
      console.log('Refreshing invoices due to navigation state - clearing cache first');
      // Clear the refresh flag immediately to prevent infinite refreshes
      navigate(location.pathname, { replace: true, state: {} });
      // Clear API cache for invoices - use broader pattern to catch all invoice-related endpoints
      clearApiCache('/api/v1/invoices');
      clearApiCache('/invoices');
      // Force immediate refresh without debounce
      // Use setTimeout to ensure cache is cleared before fetching
      // Force fresh fetch when refreshing
      setTimeout(() => {
        fetchInvoices(currentPage, itemsPerPage, filters, true);
      }, 100);
    }
  }, [location.state, location.pathname, navigate]);
  
  // Also refresh when page becomes visible (user switches back to tab/window)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && location.pathname === '/invoices') {
        console.log('Page became visible, refreshing invoices');
        clearApiCache('/api/v1/invoices');
        clearApiCache('/invoices');
        // Use current state values to avoid stale closures
        // Force fresh fetch when page becomes visible
        fetchInvoices(currentPage, itemsPerPage, filters, true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location.pathname]); // Only depend on pathname to avoid recreating listener

  // Memoize the edit form data to prevent unnecessary re-renders
  const editFormData = useMemo(() => {
    if (currentInvoice && currentView === PageView.EDIT) {
      console.log('Memoizing edit form data with invoice data:', currentInvoice);
      
      // Use the helper function to prepare form data
      const formData = prepareFormDataFromInvoice(currentInvoice);
      
      console.log('Memoized form data for EditInvoiceForm:', formData);
      
      return formData;
    }
    return null;
  }, [currentInvoice, currentView]); // Remove prepareFormDataFromInvoice dependency to prevent circular deps

  // Effect to fetch invoice data when editingInvoiceId changes
  useEffect(() => {
    if (editingInvoiceId && currentView === PageView.EDIT) {
      handleEditInvoiceForm(editingInvoiceId);
    }
  }, [editingInvoiceId, currentView]); // Remove handleEditInvoiceForm from dependencies to prevent infinite loops

  // Render the list view
  const renderListView = () => {
    return (
      <div style={{ marginTop: '-1px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', padding: '8px 32px' }}>
          <StyledBackButton onClick={handleBack}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </StyledBackButton>
          <PageTitle style={{ marginLeft: '12px', margin: '0' }}>Invoices</PageTitle>
        </div>
        <PageContainer>
          <HeaderContainer>
            <div></div>
          </HeaderContainer>

          <ControlsContainer>
            <SearchContainer>
              <SearchIconWrapper>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </SearchIconWrapper>
              <SearchInput
                type="text"
                placeholder="Search a Company"
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </SearchContainer>

            <DatePickerWrapper>
              <DateRangePicker
                value={selectedDateRange}
                onChange={handleDateRangeChange}
              />
            </DatePickerWrapper>

            {(user?.role === 'admin' || (user?.role === 'manager' && ((user as any)?.can_access_all_stores || (user as any)?.canAccessAllStores))) && (
              <StoreTypeDropdown
                value={storeTypeFilter}
                onChange={handleStoreTypeFilterChange}
              >
                <option value="All">All</option>
                <option value="Cranbourne">Cranbourne</option>
                <option value="Sale">Sale</option>
              </StoreTypeDropdown>
            )}

            <CreateButton onClick={handleCreateInvoice}>
              <img src={invoicesCreateIcon} alt="Create" />
              Create Invoice
            </CreateButton>
          </ControlsContainer>

          {/* Display error above the table if there is one and it's not a loading message */}
          {error && !error.includes("Loading data, please wait") && !isLoading && (
            <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#FEF2F2', color: '#B91C1C', borderRadius: '0.5rem' }}>
              {error}
            </div>
          )}



          <Table>
            <TableHeader>
              <tr>
                <th style={{ width: '140px', fontSize: '14px', fontWeight: 'bold', color: '#042b41' }}>
                  Invoice No
                </th>
                <th style={{ width: '200px', fontSize: '14px', fontWeight: 'bold', color: '#042b41' }}>
                  Company Name
                </th>
                <th style={{ width: '120px', fontSize: '14px', fontWeight: 'bold', color: '#042b41' }}>
                  Store Type
                </th>
                <th style={{ width: '100px', fontSize: '14px', fontWeight: 'bold', color: '#042b41' }}>
                  Date
                </th>
                <th style={{ width: '120px', fontSize: '14px', fontWeight: 'bold', color: '#042b41', textAlign: 'right' }}>
                  Grand Total (AUD)
                </th>
                <th style={{ width: '80px', fontSize: '14px', fontWeight: 'bold', color: '#042b41' }}>
                  Status
                </th>
                <th style={{ width: '60px', fontSize: '14px', fontWeight: 'bold', color: '#042b41' }}>
                  Notes
                </th>
                <th style={{ width: '140px', fontSize: '14px', fontWeight: 'bold', color: '#042b41' }}>
                  Actions
                </th>
              </tr>
            </TableHeader>
            <TableBody>
              {renderTableContent()}
            </TableBody>
          </Table>

          {!isLoading && invoices.length > 0 && (
            <PaginationContainer>
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalItems / itemsPerPage)}
                onPageChange={handlePageChange}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[10, 20, 50]}
                showItemsPerPage={true}
              />
            </PaginationContainer>
          )}

          {showNotesModal && (
            <NotesModalOverlay>
              <NotesModalContent>
                <NotesModalTitle>Invoice Notes</NotesModalTitle>
                <NotesTextarea
                  value={notesValue}
                  onChange={(e) => setNotesValue(e.target.value)}
                  placeholder="Enter notes for this invoice"
                />
                <NotesButtonContainer>
                  <CloseNotesButton onClick={() => setShowNotesModal(false)}>
                    Cancel
                  </CloseNotesButton>
                  <SaveNotesButton onClick={handleSaveNotes}>
                    {isSavingNotes ? 'Saving' : 'Save Notes'}
                  </SaveNotesButton>
                </NotesButtonContainer>
              </NotesModalContent>
            </NotesModalOverlay>
          )}
        </PageContainer>
      </div>
    );
  };

  // Simplified loading state without search-specific loading
  const renderTableContent = () => {
    // Show loading state only for initial data load
    if (isLoading) {
      return (
        <LoadingRow>
          <td colSpan={8}>
            <LoadingContainer>
              <LoadingSpinner size="lg" />
              <LoadingText>Loading invoices</LoadingText>
            </LoadingContainer>
          </td>
        </LoadingRow>
      );
    }

    // Show no results message
    if (invoices.length === 0) {
      const hasActiveSearch = searchQuery && searchQuery.trim().length > 0;
      return (
        <tr>
          <td colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
            {hasActiveSearch ? 
              `No invoices found matching "${searchQuery}"` : 
              'No invoices found.'
            }
          </td>
        </tr>
      );
    }

    return invoices.map(invoice => (
      <tr key={invoice.id}>
        <td>{invoice.invoiceNo}</td>
        <td>{invoice.customerName}</td>
        <td>{invoice.storeType}</td>
        <td>{invoice.date}</td>
        <td style={{ textAlign: 'right' }}>${invoice.total}</td>
        <td>
          <StatusBadge status={invoice.status || 'saved'}>
            {invoice.status === 'draft' ? 'DRAFTED' : 'SAVED'}
          </StatusBadge>
        </td>
        <NotesColumn
          hasNotes={invoice.hasNotes}
          onClick={() => handleViewNotes(invoice.id)}
        >
          <img src={invoicesNotesIcon} alt="Notes" />
        </NotesColumn>
        <td>
          <ActionButtonsContainer>
            <ActionButton 
              onClick={() => handleEditInvoice(invoice.id)} 
              title="Edit"
              disabled={invoice.status !== 'draft'}
            >
              <img src={invoicesEditIcon} alt="Edit" />
            </ActionButton>
            <ActionButton 
              onClick={() => handleDeleteInvoice(invoice.id, invoice.invoiceNo)} 
              title="Delete"
              disabled={invoice.status !== 'draft'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </ActionButton>
            <ActionButton onClick={() => handleDownloadInvoice(invoice.id)} title="Download PDF">
              <img src={invoicesPrintIcon} alt="Download PDF" />
            </ActionButton>
            <ActionButton 
              onClick={() => handleEmailInvoice(invoice.id)} 
              title="Email"
              disabled={invoice.status === 'draft'}
            >
              {emailingInvoiceId === invoice.id ? (
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  border: '2px solid #042B41', 
                  borderTop: '2px solid transparent', 
                  borderRadius: '50%', 
                  animation: 'spin 1s linear infinite' 
                }}></div>
              ) : (
                <img src={invoicesMailIcon} alt="Email" />
              )}
            </ActionButton>
          </ActionButtonsContainer>
        </td>
      </tr>
    ));
  };

  // Handle store type filter change
  const handleStoreTypeFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedStoreType = e.target.value;
    setStoreTypeFilter(selectedStoreType);
    
    // Reset to first page when changing store type
    setCurrentPage(1);
  };

  return (
    <Layout>
      {/* Toast notification */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => { }}
        />
      )}

      {currentView === PageView.LIST && renderListView()}
      {currentView === PageView.EDIT && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', padding: '8px 32px' }}>
            <StyledBackButton onClick={handleCloseInvoice}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </StyledBackButton>
            <BreadcrumbContainer style={{ marginLeft: '12px', margin: '0' }}>
              <BreadcrumbItem>
                <span
                  style={{ color: '#042B41', textDecoration: 'none', cursor: 'pointer' }}
                  onClick={handleCloseInvoice}
                >
                  {(() => {
                    const urlParams = new URLSearchParams(location.search);
                    return urlParams.get('returnTo') ? 'Customer History' : 'Invoice';
                  })()}
                </span>
              </BreadcrumbItem>
              <BreadcrumbSeparator>&gt;</BreadcrumbSeparator>
              <BreadcrumbItem>Edit Invoice</BreadcrumbItem>
            </BreadcrumbContainer>
          </div>
          <div style={{ padding: '0 32px' }}>
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <LoadingSpinner size="lg" />
                <span style={{ marginLeft: '12px', fontSize: '16px', color: '#6B7280' }}>Loading invoice data...</span>
              </div>
            ) : (
              <EditInvoiceForm
                initialData={editFormData || undefined}
                onSubmit={handleSubmitInvoice}
                onSaveAsDraft={handleSaveAsDraft}
                onCancel={handleCloseInvoice}
                isLoading={isLoading}
                onHasChanges={setHasUnsavedChanges}
              />
            )}
          </div>
        </>
      )}
      {currentView === PageView.CREATE && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0px', padding: '8px 32px' }}>
            <StyledBackButton onClick={handleCloseInvoice}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </StyledBackButton>
            <BreadcrumbContainer style={{ marginLeft: '12px', margin: '0' }}>
              <BreadcrumbItem>
                <span
                  style={{ color: '#042B41', textDecoration: 'none', cursor: 'pointer' }}
                  onClick={handleCloseInvoice}
                >
                  Invoice
                </span>
              </BreadcrumbItem>
              <BreadcrumbSeparator>&gt;</BreadcrumbSeparator>
              <BreadcrumbItem>Create New Invoice</BreadcrumbItem>
            </BreadcrumbContainer>
          </div>
          <div style={{ padding: '0 32px' }}>
            <EditInvoiceForm
              onSubmit={handleSubmitInvoice}
              onSaveAsDraft={handleSaveAsDraft}
              onCancel={handleCloseInvoice}
              onHasChanges={setHasUnsavedChanges}
            />
          </div>
        </>
      )}

      {/* Invoice View Modal for PDF Download */}
      {isViewInvoiceOpen && viewInvoiceId && (
        <InvoiceView
          invoiceId={viewInvoiceId}
          onClose={handleCloseInvoiceView}
        />
      )}

      {/* Exit Confirmation Dialog */}
      <ExitConfirmationDialog
        isOpen={showExitConfirmation}
        onConfirm={handleConfirmExit}
        onCancel={handleCancelExit}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={showDeleteConfirmation}
        onConfirm={handleConfirmDeleteInvoice}
        onCancel={() => {
          setShowDeleteConfirmation(false);
          setInvoiceToDelete(null);
        }}
        invoiceNo={invoiceToDelete?.invoiceNo || ''}
      />
    </Layout>
  );
};

export default InvoicesPage;